import React from 'react';
import { Avatar } from '@mui/material';

import * as ItemUtils from '../common/ItemTools';
import { buildMediaTypeNameMap } from '../common/MediaTypes';
import { EntityHoverCard, useHoverCard } from './HoverCard';
import { SERIF_STACK } from './common/tokens';

const typeNameMap = buildMediaTypeNameMap();

// A site is worth a hovercard as soon as it can be identified.
function siteHasPreview(site) {
  return Boolean(site && (site.domain || site.title));
}

// First letter of the site, used until (or instead of) its logo loads.
function siteLetter(name) {
  const stripped = name.replace(/^(www|m)\./, '');
  return stripped ? stripped[0].toUpperCase() : '?';
}

function SiteHoverCard({ site, onOpen, onFilter }) {
  const name = ItemUtils.getSiteTitle(site) || site.domain || '?';
  const logoUrl = (site.logo || site.domain) ? ItemUtils.getSiteImageUrl(site) : null;
  const { excerpt, domain, type: typeVal } = site;
  const stats = site.stats || {};
  const typeLabel = typeVal != null && typeNameMap[typeVal] ? typeNameMap[typeVal].label : null;
  const published = site.timePublished || site.timeCreated;
  const publishedLabel = published ? ItemUtils.convertUnixTimeToMoment(published) : null;
  const itemsLabel = stats.totalItems
    ? `${stats.totalItems.toLocaleString('en-US')} item${stats.totalItems === 1 ? '' : 's'}`
    : null;
  const totalTimeLabel = stats.timeNeeded
    ? ItemUtils.humanizeIntervalInMinutes(stats.timeNeeded)
    : null;
  // The title often is the domain; only repeat it when it adds something.
  const domainLabel = domain && domain !== name ? domain : null;
  const subheader = [domainLabel, typeLabel, publishedLabel, itemsLabel, totalTimeLabel]
    .filter(Boolean).join(' · ');

  return (
    <EntityHoverCard
      avatar={(
        <Avatar
          src={logoUrl}
          variant="rounded"
          sx={{
            width: 56,
            height: 56,
            flexShrink: 0,
            borderRadius: '14px',
            background: '#fff',
            border: '1px solid #e5e5e5',
            fontFamily: SERIF_STACK,
            fontWeight: 700,
            '& .MuiAvatar-img': { objectFit: 'contain' },
          }}
        >
          {siteLetter(name)}
        </Avatar>
      )}
      name={name}
      subheader={subheader}
      excerpt={excerpt}
      openLabel="View site page"
      onOpen={onOpen}
      onFilter={onFilter}
    />
  );
}

export default function useSiteHoverCard(site, {
  onCardOpen, onCardFilter, placement = 'bottom-start',
} = {}) {
  const merged = ItemUtils.mergeSite(site) || site;
  const hasCard = siteHasPreview(merged);

  return useHoverCard({
    hasCard,
    placement,
    renderCard: (close) => (
      <SiteHoverCard
        site={merged}
        onOpen={onCardOpen ? () => { close(); onCardOpen(); } : undefined}
        onFilter={onCardFilter ? () => { close(); onCardFilter(); } : undefined}
      />
    ),
  });
}
